import React, { useEffect, useState } from 'react';
import {
    Box,
    Typography,
    Paper,
    TextField,
    InputAdornment,
    IconButton,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    CircularProgress,
    Alert,
    Stack,
    useMediaQuery,
    Chip,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import Diversity3RoundedIcon from '@mui/icons-material/Diversity3Rounded';
import PaidRoundedIcon from '@mui/icons-material/PaidRounded';
import { styled, useTheme } from '@mui/system';
import { API_URL } from '../../config';

const Container = styled(Box)(({ theme }) => ({
    padding: theme.spacing(3),
    maxWidth: 960,
    margin: '0 auto',
    marginTop: theme.spacing(10),
    [theme.breakpoints.down('sm')]: {
        padding: theme.spacing(2),
        marginTop: theme.spacing(8),
    },
}));

export default function MyReferralsPage() {
    const theme = useTheme();
    const isSm = useMediaQuery(theme.breakpoints.down('sm'));

    const [base36Id, setBase36Id] = useState('');
    const [referrals, setReferrals] = useState([]);
    const [loading, setLoading] = useState(true);
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        async function loadData() {
            try {
                const res = await fetch(API_URL + 'get_referrals.php', { credentials: 'include' });
                if (!res.ok) throw new Error(res.statusText);
                const json = await res.json();
                setBase36Id(json.base36_id);
                setReferrals(json.referrals || []);
            } catch (err) {
                console.error(err);
            } finally {
                setLoading(false);
            }
        }
        loadData();
    }, []);

    const referralLink = `${window.location.origin}/r/${base36Id}`;

    const totalCommission = referrals.reduce((sum, referral) => {
        const parsed = Number.parseFloat(String(referral.commission).replace(/[^0-9.-]/g, ''));
        return sum + (Number.isNaN(parsed) ? 0 : parsed);
    }, 0);

    const handleCopy = () => {
        navigator.clipboard.writeText(referralLink)
            .then(() => {
                setCopied(true);
                setTimeout(() => setCopied(false), 3000);
            })
            .catch(console.error);
    };

    return (
        <Container>
            <Stack spacing={2.5}>
                <Typography variant={isSm ? 'h4' : 'h3'} sx={{ fontWeight: 800 }}>
                    Referral Hub
                </Typography>
                <Typography color="text.secondary" sx={{ maxWidth: 640 }}>
                    Share your personal referral link and track subscribed businesses and commissions in one premium dashboard.
                </Typography>

                <Stack direction={isSm ? 'column' : 'row'} spacing={2}>
                    <Paper sx={{ p: 2.4, flex: 1 }}>
                        <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                            <Diversity3RoundedIcon color="primary" />
                            <Typography variant="subtitle2" color="text.secondary">Subscribed Referrals</Typography>
                        </Stack>
                        <Typography variant="h4" sx={{ fontWeight: 800 }}>{referrals.length}</Typography>
                    </Paper>
                    <Paper sx={{ p: 2.4, flex: 1 }}>
                        <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                            <PaidRoundedIcon color="secondary" />
                            <Typography variant="subtitle2" color="text.secondary">Total Commission</Typography>
                        </Stack>
                        <Typography variant="h4" sx={{ fontWeight: 800 }}>${totalCommission.toFixed(2)}</Typography>
                    </Paper>
                </Stack>

                <Paper sx={{ p: { xs: 1.3, sm: 2 } }}>
                    <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 700 }}>Your Referral Link</Typography>
                    <TextField
                        fullWidth
                        size={isSm ? 'small' : 'medium'}
                        value={referralLink}
                        InputProps={{
                            readOnly: true,
                            sx: { fontSize: isSm ? '0.84rem' : '1rem', pr: 0.4 },
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton onClick={handleCopy} edge="end" color="primary" aria-label="Copy referral link">
                                        <ContentCopyIcon />
                                    </IconButton>
                                </InputAdornment>
                            )
                        }}
                    />
                </Paper>

                {copied && <Alert severity="success" variant="filled">Link copied to clipboard.</Alert>}

                <Typography variant={isSm ? 'h6' : 'h5'} sx={{ fontWeight: 700 }}>
                    Subscribed Referrals
                </Typography>

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                        <CircularProgress />
                    </Box>
                ) : referrals.length === 0 ? (
                    <Paper sx={{ p: 3 }}>
                        <Typography color="text.secondary">No referrals yet. Start sharing your link to grow your network.</Typography>
                    </Paper>
                ) : isSm ? (
                    <Stack spacing={1.5}>
                        {referrals.map((r, idx) => (
                            <Paper key={idx} sx={{ p: 2 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{r.businessName}</Typography>
                                <Stack direction="row" spacing={1} mt={1}>
                                    <Chip label={`Package: ${r.packageName}`} size="small" />
                                    <Chip label={`Commission: ${r.commission}`} size="small" color="secondary" />
                                </Stack>
                            </Paper>
                        ))}
                    </Stack>
                ) : (
                    <Paper sx={{ overflow: 'hidden' }}>
                        <Box sx={{ width: '100%', overflowX: 'auto' }}>
                            <Table size="medium">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Business Name</TableCell>
                                        <TableCell>Package</TableCell>
                                        <TableCell align="right">Commission</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {referrals.map((r, idx) => (
                                        <TableRow key={idx} hover>
                                            <TableCell sx={{ fontWeight: 600 }}>{r.businessName}</TableCell>
                                            <TableCell>{r.packageName}</TableCell>
                                            <TableCell align="right">{r.commission}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </Box>
                    </Paper>
                )}
            </Stack>
        </Container>
    );
}
